import {
  isRuntimeEventReadyForRetry,
  loadRuntimeOutbox,
  markRuntimeEventFailed,
  markRuntimeEventLocalOnly,
  markRuntimeEventSynced,
  markRuntimeEventSyncing,
  pruneRuntimeOutbox,
  recoverInterruptedRuntimeEvents,
} from './outbox';
import { submitSupabaseTrustedAttempt, uploadSupabaseInkDocument } from './supabaseAdapter';
import type { TrustedAttemptSubmissionV1, TrustedOutboxEnvelopeV1 } from './trustedRuntime';
import type { InkDocumentV1 } from '../../types/ink';

function looksLikeUuid(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function normalizeEnvelope(payload: unknown): TrustedOutboxEnvelopeV1 {
  const candidate = payload as Partial<TrustedOutboxEnvelopeV1> & Partial<TrustedAttemptSubmissionV1>;
  if (candidate && typeof candidate === 'object' && 'submission' in candidate && candidate.submission) {
    return candidate as TrustedOutboxEnvelopeV1;
  }
  return { schemaVersion: 1, submission: payload as TrustedAttemptSubmissionV1 };
}

async function syncInkIfPresent(input: {
  studentId: string;
  attemptAnswerId?: string | null;
  inkDocument?: InkDocumentV1;
}) {
  if (!input.inkDocument) return;
  if (!looksLikeUuid(input.studentId) || !looksLikeUuid(input.attemptAnswerId)) {
    throw new Error('Cloud Ink membutuhkan student UUID dan authoritative attemptAnswerId.');
  }
  await uploadSupabaseInkDocument({
    studentId: input.studentId,
    attemptAnswerId: input.attemptAnswerId,
    document: input.inkDocument,
  });
}

export type RuntimeSyncSummary = {
  synced: number;
  skipped: number;
  failed: number;
  deferred: number;
  offline: boolean;
};

export async function syncTrustedAttemptOutbox(options: { maxBatch?: number } = {}): Promise<RuntimeSyncSummary> {
  recoverInterruptedRuntimeEvents();
  const maxBatch = Math.max(1, Math.min(options.maxBatch ?? 20, 100));
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  if (offline) return { synced: 0, skipped: 0, failed: 0, deferred: 0, offline: true };

  const now = Date.now();
  const allCandidates = loadRuntimeOutbox().filter((event) =>
    event.eventType === 'trusted_attempt_submission' && (event.status === 'pending' || event.status === 'failed')
  );
  const ready = allCandidates.filter((event) => isRuntimeEventReadyForRetry(event, now));
  const deferred = allCandidates.length - ready.length;
  const candidates = ready.slice(0, maxBatch);

  let synced = 0;
  let skipped = 0;
  let failed = 0;

  for (const event of candidates) {
    const envelope = normalizeEnvelope(event.payload);
    const payload = envelope.submission;

    if (!looksLikeUuid(payload.studentRef) || !looksLikeUuid(payload.assignmentRef) || !looksLikeUuid(payload.questionRef)) {
      markRuntimeEventLocalOnly(event.id);
      skipped += 1;
      continue;
    }

    markRuntimeEventSyncing(event.id);
    try {
      const result = await submitSupabaseTrustedAttempt(payload);
      if (result.status === 'accepted' || result.status === 'duplicate') {
        await syncInkIfPresent({
          studentId: payload.studentRef,
          attemptAnswerId: result.artifacts?.attemptAnswerId,
          inkDocument: envelope.inkDocument,
        });
        markRuntimeEventSynced(event.id);
        synced += 1;
      } else {
        markRuntimeEventFailed(event.id, result.message ?? `Server returned ${result.status}`);
        failed += 1;
      }
    } catch (error) {
      markRuntimeEventFailed(event.id, error instanceof Error ? error.message : 'Unknown sync error');
      failed += 1;
    }
  }

  pruneRuntimeOutbox();
  return { synced, skipped, failed, deferred, offline: false };
}
